import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { FaTrash, FaEllipsisH, FaChartLine } from 'react-icons/fa';
import { Wifi, Wallet, TrendingUp, ChevronLeft, Plus, X, Trash2 } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import {
  AreaChart, Area, XAxis, Tooltip, ResponsiveContainer
} from 'recharts';
import toast from 'react-hot-toast';
import TableView from '../TableView';
import { QuickActionsMenu } from '../common';
import EditableTextCell from './EditableTextCell';
import CustomSelect from './CustomSelect';
import ClearMonthModal from './ClearMonthModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import GastosPorCentroCosto from './GastosPorCentroCosto';
import TendenciaAnualModal from './TendenciaAnualModal';
import '../AssetsView.css';
import { SURA_BLUE, SURA_BLUE2, SURA_GREEN, mesesVisibles, serieAnualItem } from './palette';

const MESES_ORDEN = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-xl p-3 min-w-[140px]">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex justify-between gap-3 py-0.5">
          <span className="text-xs text-gray-600">Gasto</span>
          <span className="text-xs font-bold text-gray-900">${Number(p.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </div>
      ))}
    </div>
  );
};

const PresupuestoStarlinkView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [concepto, setConcepto] = useState(null);
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);

  const mesActual = MESES_ORDEN[new Date().getMonth()];

  const [mesSeleccionado, setMesSeleccionado] = useState(mesActual);
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [nuevaAntena, setNuevaAntena] = useState({
    ubicacion: '', serial: '', correo_asociado: '', pais_registro: '', departamento: 'Presidencia'
  });
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);
  const [antenaAEliminar, setAntenaAEliminar] = useState(null);
  const [trendItem, setTrendItem] = useState(null); // { titulo, subtitulo, data }

  useEffect(() => { fetchData(); }, [id, mesSeleccionado, anioSeleccionado]); // eslint-disable-line

  const fetchData = async () => {
    setLoading(true);
    try {
      const [conceptosRes, equiposRes] = await Promise.all([
        axiosInstance.get('/api/presupuestos/conceptos'),
        axiosInstance.get(`/api/presupuestos/starlink/${id}?mes=${mesSeleccionado}&anio=${anioSeleccionado}`)
      ]);
      const currentConcepto = conceptosRes.data.find(c => c.id === parseInt(id));
      setConcepto(currentConcepto);
      setEquipos(equiposRes.data);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleClearMonth = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmClearMonth = async () => {
    try {
      await axiosInstance.delete(`/api/presupuestos/clear/starlink/${id}?mes=${mesSeleccionado}&anio=${anioSeleccionado}`);
      toast.success(`Datos de ${mesSeleccionado} eliminados correctamente.`);
      setIsDeleteModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar los datos del mes');
    }
  };

  const handleGuardarGastoMes = async (gastoId, equipoId, monto) => {
    try {
      const val = parseFloat(monto);
      if (isNaN(val)) return;

      let data;
      if (gastoId) {
        ({ data } = await axiosInstance.put(`/api/presupuestos/starlink/gasto/${gastoId}`, { monto_dolar: val }));
        toast.success("Monto actualizado");
      } else {
        const payload = { mes: mesSeleccionado, anio: anioSeleccionado, monto_dolar: val };
        ({ data } = await axiosInstance.post(`/api/presupuestos/starlink/equipo/${equipoId}/gasto`, payload));
        toast.success("Monto registrado");
      }
      // Actualiza tanto el gasto del mes actual como el histórico (gastos[]),
      // para que la gráfica de Tendencia Anual refleje el cambio de inmediato.
      setEquipos(prev => prev.map(eq => {
        if (eq.id !== equipoId) return eq;
        const gastos = eq.gastos ? [...eq.gastos] : [];
        const idx = gastos.findIndex(g => g.id === data.id || (g.mes === data.mes && g.anio === data.anio));
        if (idx >= 0) gastos[idx] = data; else gastos.push(data);
        return { ...eq, gasto: data, gastos };
      }));
    } catch (error) {
      toast.error("Error al actualizar monto");
    }
  };

  const handleUpdateEquipo = async (equipoId, field, value) => {
    try {
      await axiosInstance.put(`/api/presupuestos/starlink/equipo/${equipoId}`, { [field]: value });
      setEquipos(prev => prev.map(eq => eq.id === equipoId ? { ...eq, [field]: value } : eq));
      toast.success("Dato actualizado");
    } catch (error) {
      toast.error("Error al actualizar antena");
    }
  };

  const handleDeleteAntena = async (equipoId) => {
    try {
      await axiosInstance.delete(`/api/presupuestos/starlink/equipo/${equipoId}`);
      toast.success('Antena eliminada');
      setEquipos(prev => prev.filter(eq => eq.id !== equipoId));
    } catch (error) {
      toast.error('Error al eliminar la antena');
    } finally {
      setAntenaAEliminar(null);
    }
  };

  const handleAddAntena = async (e) => {
    e.preventDefault();
    if (!nuevaAntena.ubicacion.trim()) return toast.error("La ubicación es requerida");
    try {
      const { data } = await axiosInstance.post(`/api/presupuestos/starlink/${id}/equipo`, nuevaAntena);
      setEquipos([...equipos, data]);
      setShowAddModal(false);
      setNuevaAntena({ ubicacion: '', serial: '', correo_asociado: '', pais_registro: '', departamento: 'Presidencia' });
      toast.success("Antena agregada");
    } catch (error) {
      toast.error("Error al agregar antena");
    }
  };

  const columns = [
    {
      header: 'Ubicación',
      accessorKey: 'ubicacion',
      cell: ({ row }) => (
        <div className="font-semibold text-gray-800 text-sm w-48">
          <EditableTextCell
            value={row.original.ubicacion}
            onSave={(val) => handleUpdateEquipo(row.original.id, 'ubicacion', val)}
            placeholder="Ubicación"
          />
        </div>
      )
    },
    {
      header: 'Serial',
      accessorKey: 'serial',
      cell: ({ row }) => (
        <div className="font-mono text-sm text-gray-500 w-48">
          <EditableTextCell
            value={row.original.serial}
            onSave={(val) => handleUpdateEquipo(row.original.id, 'serial', val)}
            placeholder="Serial"
          />
        </div>
      )
    },
    {
      header: 'Correo Asociado',
      accessorKey: 'correo_asociado',
      cell: ({ row }) => (
        <div className="text-sm text-gray-600 w-56">
          <EditableTextCell
            value={row.original.correo_asociado}
            onSave={(val) => handleUpdateEquipo(row.original.id, 'correo_asociado', val)}
            placeholder="Correo"
          />
        </div>
      )
    },
    {
      header: 'País de Registro',
      accessorKey: 'pais_registro',
      cell: ({ row }) => (
        <div className="text-sm text-gray-600 w-32">
          <EditableTextCell
            value={row.original.pais_registro}
            onSave={(val) => handleUpdateEquipo(row.original.id, 'pais_registro', val)}
            placeholder="País"
          />
        </div>
      )
    },
    {
      header: 'Departamento',
      accessorKey: 'departamento',
      cell: ({ row }) => (
        <span className="text-sm font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">{row.original.departamento}</span>
      )
    },
    {
      header: 'Monto Gasto',
      id: 'monto',
      cell: ({ row }) => (
        <div className="w-24">
          <EditableTextCell
            type="number"
            prefix="$"
            decimals={2}
            value={row.original.gasto?.monto_dolar}
            onSave={(val) => handleGuardarGastoMes(row.original.gasto?.id, row.original.id, val)}
            placeholder="0.00"
          />
        </div>
      )
    },
    {
      id: 'acciones', header: '', size: 50,
      cell: ({ row }) => (
        <div className="quick-actions-container" style={{ position: 'relative' }}>
          <button
            className={`icon-button ${activeActionMenu === row.id ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (activeActionMenu === row.id) {
                setActiveActionMenu(null);
                setMenuAnchorRect(null);
              } else {
                setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
                setActiveActionMenu(row.id);
              }
            }}
          >
            <FaEllipsisH color="#111827" />
          </button>
          {activeActionMenu === row.id && (
            <QuickActionsMenu anchorRect={menuAnchorRect}>
              {[
                { icon: FaChartLine, label: 'Ver tendencia anual', color: SURA_BLUE2, onClick: () => { setTrendItem({ titulo: row.original.ubicacion || row.original.serial || 'Antena', subtitulo: [row.original.serial, row.original.departamento].filter(Boolean).join(' · '), data: serieAnualItem(row.original.gastos, anioSeleccionado, 'monto_dolar') }); setActiveActionMenu(null); } },
                { icon: FaTrash, label: 'Eliminar Antena', color: '#EF4444', onClick: () => { setAntenaAEliminar(row.original.id); setActiveActionMenu(null); } }
              ].map((action) => (
                <button key={action.label} className="quick-action-item" onClick={action.onClick}>
                  <span className="action-icon" style={{ color: action.color }}><action.icon /></span>
                  <span className="action-label">{action.label}</span>
                </button>
              ))}
            </QuickActionsMenu>
          )}
        </div>
      )
    }
  ];

  const chartData = useMemo(() => {
    return mesesVisibles(anioSeleccionado).map(mes => {
      let total = 0;
      equipos.forEach(eq => {
        const g = eq.gastos?.find(g => g.mes === mes && g.anio === anioSeleccionado);
        if (g) total += Number(g.monto_dolar || 0);
      });
      return { name: mes.substring(0, 3), fullMonth: mes, total };
    });
  }, [equipos, anioSeleccionado]);

  const totalMonto = useMemo(() => equipos.reduce((sum, eq) => sum + (eq.gasto?.monto_dolar || 0), 0), [equipos]);

  /* Gastos por centro de costo (departamento) del mes/año seleccionado
     — equipos y su .gasto ya vienen filtrados por mes/año desde el backend */
  const ccData = useMemo(() => {
    const map = {};
    equipos.forEach(eq => {
      const monto = eq.gasto?.monto_dolar;
      if (!monto) return;
      const cc = eq.departamento || 'Sin asignar';
      map[cc] = (map[cc] || 0) + Number(monto);
    });
    return Object.entries(map)
      .map(([centro_costo, total]) => ({ centro_costo, total }))
      .sort((a, b) => b.total - a.total);
  }, [equipos]);

  if (loading) {
    return <div className="p-8 text-center text-gray-500 font-medium text-shimmer">Cargando datos de Starlink...</div>;
  }

  return (
    <div className="assets-view">

      {/* ── Hero Header ──────────────────────────────────────────── */}
      <div
        className="rounded-2xl mb-6 px-7 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative"
        style={{ background: `linear-gradient(135deg, ${SURA_BLUE} 0%, ${SURA_BLUE2} 100%)` }}
      >
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div className="absolute right-0 top-0 w-48 h-48 rounded-full opacity-10" style={{ background: '#fff', transform: 'translate(30%, -30%)' }} />
          <div className="absolute right-12 bottom-0 w-32 h-32 rounded-full opacity-5" style={{ background: '#fff', transform: 'translateY(40%)' }} />
        </div>

        <div className="flex items-center gap-4 z-10">
          <button onClick={() => navigate('/presupuestos')} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-medium transition-colors">
            <ChevronLeft size={16} /> Presupuestos
          </button>
          <div className="w-px h-5 bg-white/20" />
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
            <Wifi size={22} color="#fff" />
          </div>
          <div>
            <h2 className="text-white text-xl font-bold m-0 leading-tight flex items-center gap-2">
              {concepto?.nombre || 'Antenas Starlink'}
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-white/10 hover:bg-white/20 text-white/70 hover:text-white rounded-full p-1 border border-transparent hover:border-white/20 transition-all"
                title="Añadir antena"
              >
                <Plus size={14} />
              </button>
            </h2>
            <p className="text-white/60 text-xs m-0 mt-0.5">Gestión de gastos e inventario de antenas</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 z-10">
          <CustomSelect
            value={mesSeleccionado}
            onChange={(e) => setMesSeleccionado(e.target.value)}
            className="px-3 py-1.5 rounded-lg text-sm font-medium outline-none"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
          >
            {MESES_ORDEN.map(m => <option key={m} value={m}>{m}</option>)}
          </CustomSelect>
          <CustomSelect
            value={anioSeleccionado}
            onChange={(e) => setAnioSeleccionado(parseInt(e.target.value))}
            className="px-3 py-1.5 rounded-lg text-sm font-medium outline-none"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
          >
            {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </CustomSelect>
          <button
            onClick={handleClearMonth}
            className="bg-red-500/20 hover:bg-red-500 text-white p-1.5 rounded-lg transition-colors border border-red-500/30 hover:border-red-500 flex items-center justify-center group"
            title="Limpiar datos del mes"
          >
            <Trash2 size={16} className="text-red-300 group-hover:text-white transition-colors" />
          </button>
          <span
            className="text-xs font-semibold px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
          >
            STARLINK
          </span>
        </div>
      </div>

      {/* ── KPI Row ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[
          { title: 'Antenas Activas', value: equipos.length, icon: Wifi, color: SURA_BLUE, bg: '#EFF6FF', mono: false },
          { title: 'Gasto Total del Mes', value: `$${totalMonto.toFixed(2)}`, icon: Wallet, color: SURA_GREEN, bg: '#ECFDF5', mono: true },
        ].map(kpi => (
          <div key={kpi.title} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 relative overflow-hidden hover:shadow-md transition-shadow">
            <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: kpi.color }} />
            <div className="flex justify-between items-start mb-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{kpi.title}</p>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: kpi.bg }}>
                <kpi.icon size={17} style={{ color: kpi.color }} />
              </div>
            </div>
            <p className={`text-2xl font-bold text-gray-900 ${kpi.mono ? 'font-mono' : ''}`}>{kpi.value}</p>
          </div>
        ))}

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 relative overflow-hidden hover:shadow-md transition-shadow">
          <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: SURA_BLUE2 }} />
          <div className="flex justify-between items-center mb-1">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Tendencia Anual</p>
            <TrendingUp size={14} style={{ color: SURA_BLUE2 }} />
          </div>
          <div className="h-16 w-full mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorStarlink" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={SURA_BLUE2} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={SURA_BLUE2} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#9ca3af' }} interval="preserveStartEnd" />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="total" stroke={SURA_BLUE2} strokeWidth={2} fill="url(#colorStarlink)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ── Gastos por Centro de Costo ─────────────────────────── */}
      <div className="mb-6">
        <GastosPorCentroCosto
          data={ccData}
          subtitle={`${mesSeleccionado.charAt(0) + mesSeleccionado.slice(1).toLowerCase()} ${anioSeleccionado}`}
        />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-6">
        <TableView columns={columns} data={equipos} searchPlaceholder="Buscar antena por ubicación o serial..." />
      </div>

      {showAddModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center" style={{ background: 'linear-gradient(135deg, #003366 0%, #0062A5 100%)' }}>
              <h3 className="font-bold text-white text-lg">Añadir Nueva Antena</h3>
              <button onClick={() => setShowAddModal(false)} className="text-white/70 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddAntena} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Ubicación</label>
                <input
                  type="text"
                  required
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                  value={nuevaAntena.ubicacion}
                  onChange={e => setNuevaAntena({...nuevaAntena, ubicacion: e.target.value})}
                  placeholder="Ej. Casa Presidencia"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Serial</label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                  value={nuevaAntena.serial}
                  onChange={e => setNuevaAntena({...nuevaAntena, serial: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Correo Asociado</label>
                <input
                  type="email"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                  value={nuevaAntena.correo_asociado}
                  onChange={e => setNuevaAntena({...nuevaAntena, correo_asociado: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">País de Registro</label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                  value={nuevaAntena.pais_registro}
                  onChange={e => setNuevaAntena({...nuevaAntena, pais_registro: e.target.value})}
                />
              </div>
              <div className="pt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                  style={{ background: SURA_BLUE }}
                >
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      <ClearMonthModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmClearMonth}
        mes={mesSeleccionado}
        anio={anioSeleccionado}
        tipo="Starlink"
      />

      <ConfirmDeleteModal
        isOpen={antenaAEliminar !== null}
        onClose={() => setAntenaAEliminar(null)}
        onConfirm={() => handleDeleteAntena(antenaAEliminar)}
        title="Eliminar antena"
        message="¿Seguro que deseas eliminar esta antena?"
      />

      <TendenciaAnualModal
        isOpen={trendItem !== null}
        onClose={() => setTrendItem(null)}
        titulo={trendItem?.titulo}
        subtitulo={trendItem?.subtitulo}
        anio={anioSeleccionado}
        data={trendItem?.data || []}
      />
    </div>
  );
};

export default PresupuestoStarlinkView;
